import { useCallback, useMemo } from "react";
import {
  DistributionGroupFragment,
  MessageThreadType,
  TagFragment,
  UserFragment,
  UserRoleType,
} from "gql/graphql";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useTags } from "modules/providers/tags-provider";
import { useUsers } from "modules/providers/users-provider";
import { CALLOUT_KEY, HASH_KEY, MENTION_ALLOWED_CHARS_REGEX, MENTION_TYPE } from "modules/utils/Constants";
import { renderQuillUserMention } from "./render-quill-user-mention";
import { renderQuillDistributionGroupMention } from "./render-quill-distribution-group-mention";
import { renderQuillTagMention } from "./render-quill-tag-mention";

type MentionType = "user" | "tag" | "distribution_group";

interface IProps {
  isLocked?: boolean;
  type?: MessageThreadType;
  accessUserIdList?: string[];
  distributionGroupIdList?: string[];
}

export interface QuillMention {
  id: string;
  value: string;
  type: MENTION_TYPE;
  isExternal: boolean;
}

const useQuillMentions = ({
  isLocked,
  type,
  accessUserIdList,
  distributionGroupIdList,
}: IProps) => {
  const { activeUsers, usersById } = useUsers();
  const { activeDistributionGroups, distributionGroupsById } =
    useDistributionGroups();
  const { activeTags, tagsById } = useTags();

  const mentionTypes = useMemo((): MentionType[] => {
    switch (type) {
      case MessageThreadType.Chat:
        return ["user"];
      case MessageThreadType.Thread:
      case MessageThreadType.Task:
      default:
        return ["user", "tag", "distribution_group"];
    }
  }, [type]);

  const _userIdList = useMemo(() => {
    if (isLocked || type === MessageThreadType.Chat) {
      return accessUserIdList;
    }
  }, [accessUserIdList, isLocked, type]);

  const _distributionGroupIdList = useMemo(() => {
    if (type === MessageThreadType.Chat) {
      return [];
    }
    if (isLocked) {
      return distributionGroupIdList;
    }
  }, [distributionGroupIdList, isLocked, type]);

  const usersFilter = (user: UserFragment, searchString: string) =>
    user.lastName.toLowerCase().includes(searchString) ||
    user.firstName.toLowerCase().includes(searchString) ||
    user.callout?.toLowerCase().includes(searchString);

  const distributionGroupFilter = (
    distributionGroup: DistributionGroupFragment,
    searchString: string,
  ) =>
    distributionGroup.name.toLowerCase().includes(searchString) &&
    distributionGroup.active;

  const tagFilter = (
    tag: TagFragment & { displayName: string },
    searchString: string,
  ) => tag.displayName.toLowerCase().includes(searchString);

  const renderUser = useCallback(
    (id: string, searchTerm: string) => {
      const user = usersById[id];
      if (!user) return null;
      return renderQuillUserMention(user, searchTerm);
    },
    [usersById],
  );

  const renderDistributionGroup = useCallback(
    (id: string, searchTerm: string) => {
      const distributionGroup = distributionGroupsById[id];
      if (!distributionGroup) return null;
      return renderQuillDistributionGroupMention(distributionGroup, searchTerm);
    },
    [distributionGroupsById],
  );

  const renderTag = useCallback(
    (id: string, searchTerm: string) => {
      const tag = tagsById[id];
      if (!tag) return null;
      return renderQuillTagMention(tag, searchTerm);
    },
    [tagsById],
  );

  const renderItem = useCallback(
    (item: QuillMention, searchTerm: string) => {
      switch (item.type) {
        case MENTION_TYPE.USER:
          return renderUser(item.id, searchTerm);
        case MENTION_TYPE.DISTRIBUTION_GROUP:
          return renderDistributionGroup(item.id, searchTerm);
        default:
          return renderTag(item.id, searchTerm);
      }
    },
    [renderUser, renderDistributionGroup, renderTag],
  );

  const source = useCallback(
    (
      searchTerm: string,
      renderList: (matches: QuillMention[], searchTerm: string) => void,
      mentionChar: string,
    ) => {
      const query = searchTerm.toLowerCase();
      const mentions: QuillMention[] = [];
      if (mentionTypes.includes("user") && mentionChar === CALLOUT_KEY) {
        mentions.push(
          ...activeUsers
            .filter((u) => (_userIdList ? _userIdList.includes(u.id) : true))
            .filter((u) => !!u.callout)
            .filter((u) => usersFilter(u, query))
            .map((u) => ({
              id: u.id,
              value: u.callout ?? "",
              type: MENTION_TYPE.USER,
              isExternal: u.roleType === UserRoleType.Email,
            })),
        );
      }
      if (
        mentionTypes.includes("distribution_group") &&
        mentionChar === CALLOUT_KEY
      ) {
        mentions.push(
          ...activeDistributionGroups
            .filter((g) =>
              _distributionGroupIdList
                ? _distributionGroupIdList.includes(g.id)
                : true,
            )
            .filter((g) => distributionGroupFilter(g, query))
            .map((g) => ({
              id: g.id,
              value: g.name,
              type: MENTION_TYPE.DISTRIBUTION_GROUP,
              isExternal: g.emailUserIdList.length > 0,
            })),
        );
      }
      if (mentionTypes.includes("tag") && mentionChar === HASH_KEY) {
        mentions.push(
          ...activeTags
            .filter((t) => tagFilter(t, query))
            .map((t) => ({
              id: t.id,
              value: t.displayName,
              type: MENTION_TYPE.TAG,
              isExternal: false,
            })),
        );
      }
      mentions.sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { numeric: true }),
      );
      renderList(mentions, searchTerm);
    },
    [
      _distributionGroupIdList,
      _userIdList,
      activeDistributionGroups,
      activeTags,
      activeUsers,
      mentionTypes,
    ],
  );

  // MentionOption is overly strict in its type definition, but the library accepts a partial config at runtime
  const mentionOption = useMemo(
    () => ({
      allowedChars: MENTION_ALLOWED_CHARS_REGEX,
      mentionDenotationChars: [CALLOUT_KEY, HASH_KEY],
      source,
      renderItem,
      defaultMenuOrientation: "top",
      showDenotationChar: false,
      dataAttributes: ["id", "type"],
      isolateCharacter: true,
      positioningStrategy: "fixed",
    }),
    [source, renderItem],
  );

  return {
    mentionOption,
  };
};

export default useQuillMentions;
